import Joi from '@hapi/joi';
import React from 'react';
import { CraConditionScores } from '../../../../../common/src/enums/CraConditionScores';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchGet } from '../../../store/fetch';
import { EditPanel, EditPanelButtons, EditPanelCell, EditPanelRow, PanelBody, PanelHeader } from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import {
    ValidatingCulturedFormInputNumber,
    ValidatingDropdownToggleCRA,
    ValidatingEditPanelSaveButton,
    ValidatingForm,
    ValidatingFormTextArea,
} from '../../validatingElements';

const schema = Joi.object().keys({
    craInitialScore: Joi.number().allow(null, ''),
    craInitialUnityCheck: Joi.number().allow(null, ''),
    craInitialRemarks: Joi.string().when('craInitialScore', { is: Joi.number(), then: Joi.required(), otherwise: Joi.allow(null, '') }),
    craHistoryScore: Joi.number().allow(null, ''),
    craHistoryUnityCheck: Joi.number().allow(null, ''),
    craHistoryRemarks: Joi.string().when('craHistoryScore', { is: Joi.number(), then: Joi.required(), otherwise: Joi.allow(null, '') }),
});

interface ISurveyEditCraAnalysisProps extends ITranslateProps {
    surveyId: string;
    objectId: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    selectedName?: undefined | string;
    selectedFailureModeUpdate: (selectedFailureModeId: string) => void;
    onSaved(): void;
}

interface ISurveyEditCraAnalysisState {
    conditionId?: undefined | string;
    isLoaded: boolean;
}

export function RiskScoreDropDown(props: { name: string; postText?: string }) {
    const translateKey = useTranslate();
    return (
        <ValidatingDropdownToggleCRA
            name={props.name}
            header={translateKey('failureMode.riskScoreHeader')}
            caretVisible={false}
            postText={props.postText}
            options={[
                { text: translateKey(`cra.riskScore${CraConditionScores.LowRisk}`), value: CraConditionScores.LowRisk },
                { text: translateKey(`cra.riskScore${CraConditionScores.BelowAverageRisk}`), value: CraConditionScores.BelowAverageRisk },
                { text: translateKey(`cra.riskScore${CraConditionScores.AboveAverageRisk}`), value: CraConditionScores.AboveAverageRisk },
                { text: translateKey(`cra.riskScore${CraConditionScores.HighRisk}`), value: CraConditionScores.HighRisk },
            ]}
            directSave={false}
        />
    );
}

class SurveyEditCraAnalysisComponent extends ComponentSetStatePromised<ISurveyEditCraAnalysisProps, ISurveyEditCraAnalysisState> {
    constructor(props: ISurveyEditCraAnalysisProps) {
        super(props);
        this.state = { isLoaded: false };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override async componentDidUpdate(prevProps: ISurveyEditCraAnalysisProps) {
        const shouldReload =
            prevProps.elementId !== this.props.elementId ||
            prevProps.unitId !== this.props.unitId ||
            prevProps.manifestationId !== this.props.manifestationId;

        if (shouldReload) {
            await this.setStatePromised({ isLoaded: false });
            await this.load();
        }
    }

    public override render() {
        if (!this.state.isLoaded) {
            return null;
        }

        return (
            <PanelBody isForm={true}>
                <EditPanel>
                    <ValidatingForm
                        schema={schema}
                        createUrl={`/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition`}
                        itemUrl={`/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition/`}
                        updateUrl={`/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition/${this.state.conditionId}`}
                        id={this.state.conditionId}
                        onSubmitted={async () => this.handleSubmitted()}
                    >
                        <PanelHeader text={this.props.t('cra.initialDesignAndModifications')} />
                        <EditPanelRow>
                            <EditPanelCell rowCount={1}>
                                {' '}
                                <RiskScoreDropDown name="craInitialScore" />{' '}
                            </EditPanelCell>
                            <EditPanelCell rowCount={3}>
                                <ValidatingCulturedFormInputNumber name="craInitialUnityCheck" placeholder="Unity check" />{' '}
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <ValidatingFormTextArea size="xs" name="craInitialRemarks" placeholder={this.props.t('common.remarks')} />
                        </EditPanelRow>
                        <PanelHeader text={this.props.t('cra.historicalSurveysAndCurrentCalculations')} />
                        <EditPanelRow>
                            <EditPanelCell rowCount={1}>
                                {' '}
                                <RiskScoreDropDown name="craHistoryScore" />{' '}
                            </EditPanelCell>
                            <EditPanelCell rowCount={3}>
                                <ValidatingCulturedFormInputNumber name="craHistoryUnityCheck" placeholder="Unity check" />{' '}
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <ValidatingFormTextArea size="xs" name="craHistoryRemarks" placeholder={this.props.t('common.remarks')} />
                        </EditPanelRow>
                        <EditPanelButtons>
                            <ValidatingEditPanelSaveButton small={true} />
                        </EditPanelButtons>
                    </ValidatingForm>
                </EditPanel>
            </PanelBody>
        );
    }

    private async load() {
        const url = `/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition`;

        const result = await fetchGet(url);
        await this.setStatePromised({ conditionId: result.json.id, isLoaded: true });
    }

    private async handleSubmitted() {
        this.props.onSaved();
        if (this.state.conditionId === undefined) {
            await this.load();
        }
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyEditCraAnalysis = translate()(SurveyEditCraAnalysisComponent);
